/**
 * Safe JSON parsing utilities. Use these instead of JSON.parse when reading
 * from storage or untrusted input to avoid runtime errors on invalid data.
 */

/**
 * Parse JSON safely. Returns parsed object or array, or null if invalid.
 * Use for storage (e.g. localStorage) or any string that should be an object/array.
 *
 * @param {string} str - JSON string to parse
 * @returns {object|array|null} Parsed object/array, or null if invalid or not an object/array
 */
export function safeParse(str) {
  if (str == null || typeof str !== "string" || str.trim() === "") return null;
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Parse a value as a boolean safely. Supports JSON booleans ("true"/"false")
 * and string literals "true"/"false". Never throws.
 *
 * @param {string|unknown} value - Value to parse (e.g. menu key or query param)
 * @returns {boolean} Parsed boolean, or false if invalid
 */
export function safeParseBoolean(value) {
  if (value === true) return true;
  if (value === false) return false;
  const str = value != null ? String(value).trim() : "";
  if (str === "") return false;
  if (str === "true") return true;
  if (str === "false") return false;
  try {
    const parsed = JSON.parse(str);
    return parsed === true || parsed === false ? parsed : false;
  } catch {
    return false;
  }
}
